"use client";

import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const journeySteps = [{
  year: "1995",
  title: "The First Expedition",
  description: "Andrei and Mark's parents instill the philosophy of 'leaving it better than you found it' during their first formative journey through the vibrant streets of Cuba."
}, {
  year: "2010",
  title: "Collective Discovery",
  description: "With over 70 years of combined travel experience, the duo begins documenting hidden gems across the globe, from the Coffee Farms of Colombia to the shores of Naxos."
}, {
  year: "2018",
  title: "PS Travel is Born",
  description: "Launching a new standard of luxury boutique travel in the Caribbean, redefining the agency experience by focusing on immersive local connections rather than just destinations."
}, {
  year: "2024",
  title: "A Legacy of Giving",
  description: "Formalizing the PS Philanthropy Fund, we continue our mission to 'Travel the world while saving the world,' delivering essential supplies to the communities we love."
}];
function TimelineItem({
  step,
  index
}: {
  step: (typeof journeySteps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="125498" ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-start md:items-center justify-between mb-16 md:mb-24 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-background border-2 border-secondary transform -translate-x-[7px] md:-translate-x-1/2 mt-2 md:mt-0 transition-all duration-300 hover:scale-150 hover:bg-primary z-10 shadow-sm cursor-pointer" />
          </TooltipTrigger>
          <TooltipContent side={isEven ? "right" : "left"} sideOffset={16}>
            <p className="font-medium">Experience: {step.year}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {}
      <div className={`w-full md:w-[45%] pl-12 md:pl-0 ${isEven ? "md:text-right md:pr-16" : "md:ml-auto md:pl-16"}`}>
        <Card className="bg-card/60 backdrop-blur-md border-border/40 shadow-sm hover:shadow-md transition-all duration-500 group overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <CardContent className="p-6 md:p-8 relative z-10">
            <span className="block font-default text-4xl md:text-5xl font-bold text-secondary mb-4 group-hover:text-primary transition-colors duration-300">
              {step.year}
            </span>
            <h3 className="text-xl md:text-2xl font-default font-semibold text-foreground mb-3 tracking-tight">
              {step.title}
            </h3>
            <p className="text-muted-foreground font-sans leading-relaxed text-sm md:text-base">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function OurClinicalJourney() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="legacy-of-care" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-6xl">
        <div ref={headerRef} className={`text-center mb-20 md:mb-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
            Legacy of Care
          </h2>
          <p className="text-lg font-sans text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Tracing seven decades of combined global exploration and our unwavering commitment to leaving the world better than we found it.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-4 md:left-1/2 top-2 bottom-2 w-0.5 bg-primary/40 md:-translate-x-1/2 rounded-full shadow-[0_0_10px_rgba(20,184,166,0.3)]" />

          {}
          <div className="relative">
            {journeySteps.map((step, index) => <TimelineItem key={index} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}